/**
 * CetusEd Community Portal — Compose Message
 *
 * COMPLIANCE: Messages sent through the portal are part of the student's
 *   education record. Staff replies may constitute prior written notice (PWN)
 *   under IDEA §300.503. All messages are retained per the district's record
 *   retention policy (typically 7 years under FERPA §1232g(e)).
 */

import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSendMessage } from '../../api/hooks';
import { useAuth } from '../../auth/useAuth';
import { Button } from '../../ui/components/Button';

export const Compose: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const sendMessage = useSendMessage();

  const [selectedStudentId, setSelectedStudentId] = useState(
    auth.user?.linkedStudents[0]?.id || '',
  );
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!subject.trim() || !body.trim() || !selectedStudentId) return;

      // mutate() routes failures into sendMessage.isError, which is rendered
      // below — no swallowed rejection, no invisible failure.
      sendMessage.mutate(
        {
          to: selectedStudentId,
          subject: subject.trim(),
          body: body.trim(),
        },
        { onSuccess: () => navigate('/messages', { replace: true }) },
      );
    },
    [subject, body, selectedStudentId, sendMessage, navigate],
  );

  if (!auth.user) return null;

  return (
    <div className="compose-page">
      <nav className="breadcrumb">
        <Link to="/messages">Messages</Link> / New Message
      </nav>

      <h1>New Message</h1>

      <form onSubmit={handleSubmit} className="compose-form">
        <div className="form-field">
          <label htmlFor="compose-student">Regarding Student</label>
          <select
            id="compose-student"
            value={selectedStudentId}
            onChange={(e) => setSelectedStudentId(e.target.value)}
            required
          >
            <option value="">Select a student</option>
            {auth.user.linkedStudents.map((student) => (
              <option key={student.id} value={student.id}>
                {student.name} — {student.school}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="compose-subject">Subject</label>
          <input
            id="compose-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            maxLength={200}
          />
        </div>

        <div className="form-field">
          <label htmlFor="compose-body">Message</label>
          <textarea
            id="compose-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            rows={8}
            maxLength={10000}
          />
        </div>

        {sendMessage.isError && (
          <div className="form-error" role="alert">
            Your message could not be sent. Please try again.
          </div>
        )}

        <div className="compose-actions">
          <Button type="submit" disabled={sendMessage.isPending} isLoading={sendMessage.isPending}>
            Send Message
          </Button>
          <Link to="/messages">
            <Button variant="ghost">Cancel</Button>
          </Link>
        </div>
      </form>
    </div>
  );
};
